/**
 * @voltxampere/engine — Inductor DC stamp
 *
 * In DC steady state an inductor is a short circuit — `V = L di/dt`
 * goes to zero when `di/dt = 0`. We model this with the same MNA
 * augmentation as a 0-volt independent voltage source: an extra
 * row/col `k = N + vSourceIndex(componentId)`, four `±1` cells
 * connecting the two terminals to that row/col, and `rhs[k] = 0`.
 *
 * The MNA planner therefore counts inductors *as voltage sources*
 * when sizing M. The pseudo-source's `vSourceIndex` is registered
 * during the planning phase under the inductor's component id.
 *
 * Sprint 6's transient solver replaces this stamp with a companion
 * model whose `Geq` and `Ieq` depend on the integration method.
 * `initialCurrent` is recognised for forward compatibility but
 * ignored at DC.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { defineDCStamp } from "../definition.js";
import { getTerminalNetId } from "../terminal-utils.js";
export const inductorDCStamp = (ctx, component) => {
    const a = ctx.nodeIndex(getTerminalNetId(component, "p1"));
    const b = ctx.nodeIndex(getTerminalNetId(component, "p2"));
    const k = ctx.vSourceIndex(component.id);
    ctx.addY(a, k, 1);
    ctx.addY(b, k, -1);
    ctx.addY(k, a, 1);
    ctx.addY(k, b, -1);
    // rhs[k] += 0 — explicit zero, kept here as documentation of the
    // 0-volt source equivalence.
    ctx.rhs[k] = (ctx.rhs[k] ?? 0) + 0;
};
/**
 * DC inductor is a 0-V source (short circuit). Transient inductor
 * keeps the same `["vsource"]` augmentation per SPECS §3.8 — the
 * BE/TRAP/Gear-2 companion fills the augmented row's RHS.
 */
export const inductorDCStampDefinition = defineDCStamp("inductor", inductorDCStamp, ["vsource"]);
//# sourceMappingURL=inductor.js.map